import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from '~/data/http/axios'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import i18n from '~/i18n'
import EmailValidator from '~/Classes/emailValidator'
import TemplateImageLoader from '~/pages/cp/automation/addNew/templateImageLoader.jsx'
import SiteVars from '~/Classes/siteVars'
import { alterSnackbar } from '~/data/actions/siteActions'

export default function AbSendPreview({ campaigns, history }) {
    const dispatch = useDispatch()
    const user = useSelector((state) => state.user)
    const accountMaster = useSelector((state) => state.accountMaster)
    const [domains, setDomains] = useState([])
    const [previewEmailAddresses, setPreviewEmailAddresses] = useState([
        { emailAddress: user?.userData?.emailAddress || '', error: '' }
    ])
    const [useVariantFrom, setUseVariantFrom] = useState({})
    const [sendingId, setSendingId] = useState(null)
    const [domainAuthErrorId, setDomainAuthErrorId] = useState(null)

    const approved = !!accountMaster?.accountMaster?.approved
    const registered = !!user?.userData?.registered
    const accountId = accountMaster?.accountMaster?.id

    useEffect(() => {
        let cancelled = false
        axios
            .get('/accountMaster/domain')
            .then((response) => {
                if (!cancelled) setDomains(response.data?.domains || [])
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])

    if (!campaigns?.length) return null

    const userEmail = user?.userData?.emailAddress || ''
    let sendingDomain = domains.find((domain) => domain.defaultPreviewDomain)
    if (!sendingDomain && userEmail.indexOf('@') > -1) {
        const domainName = userEmail.split('@')[1].toLowerCase()
        sendingDomain = domains.find((domain) => (domain.domainName || '').toLowerCase() === domainName)
    }
    const defaultFromAddress = sendingDomain?.originalEmailAddress || userEmail
    const addressesBlank = previewEmailAddresses.every((preview) => !(preview.emailAddress || '').trim())

    const updatePreview = (index, value) => {
        const next = previewEmailAddresses.slice()
        next[index] = { ...next[index], emailAddress: value, error: '' }
        setPreviewEmailAddresses(next)
        setDomainAuthErrorId(null)
    }

    const addPreview = () => {
        setPreviewEmailAddresses(previewEmailAddresses.concat([{ emailAddress: '', error: '' }]))
    }

    const sendPreview = (variant) => {
        if (sendingId || !variant?.TemplateId) return
        let hasErrors = false
        const next = previewEmailAddresses.map((preview) => {
            const emailAddress = (preview.emailAddress || '').trim()
            let error = ''
            if (emailAddress && !EmailValidator.validateEmail(emailAddress)) {
                hasErrors = true
                error = i18n.t('campaigns:standard.step3.emailInvalid')
            }
            return { ...preview, emailAddress, error }
        })
        setPreviewEmailAddresses(next)
        if (hasErrors) return

        const addresses = next.map((preview) => preview.emailAddress).filter((address) => address)
        if (!addresses.length) return

        const multiSender = Array.isArray(variant.multiAddressRules) && variant.multiAddressRules.length > 0
        const body = {
            emailAddress: addresses,
            subject: variant.subject ? 'Test: ' + variant.subject : ''
        }
        if (variant.options && variant.options.doNotTrackLinks && variant.options.doNotTrackLinks == true) {
            body.trackLinks = false
            body.trackOpens = false
        }
        body.TrackingDomainId = variant.TrackingDomainId
        body.CdnDomainId = variant.CdnDomainId
        body.preheaderText = variant.options?.preheaderText
        if (!multiSender) {
            body.fromName = variant.fromName
            const sameAddress =
                defaultFromAddress &&
                variant.fromAddress &&
                variant.fromAddress.toLowerCase() === defaultFromAddress.toLowerCase()
            const usesVariantFrom = useVariantFrom[variant.id] !== false
            if (variant.fromAddress && (sameAddress || usesVariantFrom)) {
                body.fromAddress = variant.fromAddress
            }
        }

        setSendingId(variant.id)
        setDomainAuthErrorId(null)
        dispatch(alterSnackbar(true, i18n.t('campaigns:standard.step3.sendingPreview')))
        axios
            .post('/template/preview/' + variant.TemplateId, body)
            .then(() => {
                dispatch(alterSnackbar(true, i18n.t('campaigns:standard.step3.sentPreview')))
            })
            .catch((error) => {
                if (error?.data?.error?.fromAddress) {
                    setDomainAuthErrorId(variant.id)
                }
                dispatch(alterSnackbar(true, i18n.t('campaigns:standard.step3.errorPreview')))
            })
            .then(() => setSendingId(null))
    }

    return (
        <Paper>
            <h4 className='mar-b25'>{i18n.t('campaigns:abSplitTest.send.preview.title')}</h4>
            <div className='txtInput_label'>{i18n.t('campaigns:standard.step3.sendTest')}</div>
            <div className='mar-b10'>
                {previewEmailAddresses.map((preview, index) => (
                    <div key={index} className='form-group mar-b5'>
                        <TextInput
                            type='email'
                            name='previewEmailTo'
                            disabled={!approved}
                            onChange={(event) => updatePreview(index, event.target.value)}
                            value={preview.emailAddress}
                            placeholder={i18n.t('campaigns:standard.step3.enterEmailAddressHere')}
                            error={preview.error}
                        />
                    </div>
                ))}
            </div>
            {previewEmailAddresses.length < 4 && approved && (
                <a className='quickFlex mar-b25' onClick={addPreview} style={{ alignItems: 'center' }}>
                    <Icons icon='plus' className='mar-r5' />
                    {i18n.t('templates:preview.add')}
                </a>
            )}
            {!approved && (
                <div className='mar-b25'>
                    {registered && <span>{i18n.t('campaigns:abSplitTest.send.preview.notApproved')}</span>}
                    {!registered && (
                        <div className='text-red text-heavy'>
                            {i18n.t('campaigns:abSplitTest.send.preview.mustConfirm')}
                        </div>
                    )}
                </div>
            )}
            <div className='ab-send-preview-cards'>
                {campaigns.map((variant, index) => {
                    const multiSender =
                        Array.isArray(variant.multiAddressRules) && variant.multiAddressRules.length > 0
                    const chooseFrom =
                        !multiSender &&
                        !!variant.fromAddress &&
                        (!defaultFromAddress ||
                            variant.fromAddress.toLowerCase() !== defaultFromAddress.toLowerCase())
                    const hasTemplate = !!variant.TemplateId
                    const usesVariantFrom = useVariantFrom[variant.id] !== false
                    const name =
                        variant.campaignName || i18n.t('campaigns:abSplitTest.send.test') + ' ' + (index + 1)
                    const screenshotUrl = hasTemplate
                        ? SiteVars.repoEndPoint +
                          'userContent/' +
                          accountId +
                          '/templates/screenshots/template' +
                          variant.TemplateId +
                          '.jpg'
                        : ''

                    return (
                        <div key={variant.id || index} className='ab-send-preview-card-slot'>
                            <Paper className='ab-send-preview-card'>
                                {hasTemplate && (
                                    <div className='ab-send-preview-card-image'>
                                        <TemplateImageLoader imageUrl={screenshotUrl} />
                                    </div>
                                )}
                                {!hasTemplate && (
                                    <WarningHolder className='mar-b15'>
                                        {i18n.t('campaigns:abSplitTest.send.preview.noTemplate')}
                                    </WarningHolder>
                                )}
                                <div className='text-heavy mar-b5'>{name}</div>
                                {variant.subject && variant.subject !== name && (
                                    <div className='text-sml grey mar-b15'>{variant.subject}</div>
                                )}
                                <div className='ab-send-preview-card-actions'>
                                    {(multiSender || variant.fromAddress) && (
                                        <div className='txtInput_label'>
                                            {i18n.t('campaigns:standard.step3.fromAddress')}
                                        </div>
                                    )}
                                    {multiSender && defaultFromAddress && (
                                        <div className='text-sml grey mar-b15'>
                                            {i18n.t('campaigns:abSplitTest.send.preview.multiSender', {
                                                address: defaultFromAddress
                                            })}
                                        </div>
                                    )}
                                    {approved && chooseFrom && (
                                        <Select
                                            outerClassName='mar-b15'
                                            data={[
                                                { label: variant.fromAddress, value: true },
                                                { label: defaultFromAddress, value: false }
                                            ]}
                                            value={usesVariantFrom}
                                            onChange={(event) =>
                                                setUseVariantFrom({
                                                    ...useVariantFrom,
                                                    [variant.id]: event.target.value
                                                })
                                            }
                                        />
                                    )}
                                    {!(approved && chooseFrom) && !multiSender && variant.fromAddress && (
                                        <div className='mar-b15'>{variant.fromAddress}</div>
                                    )}
                                    <Button
                                        className='button-primary'
                                        iconLeft={<Icons icon='envelope' />}
                                        text={i18n.t('campaigns:standard.step3.sendButt')}
                                        disabled={sendingId !== null || !hasTemplate || !registered || addressesBlank}
                                        onClick={() => sendPreview(variant)}
                                        postHogTag='campaign-ab-send-preview'
                                    />
                                    {domainAuthErrorId == variant.id && (
                                        <div className='errorHolderWarning mar-t15'>
                                            <div className='text-red mar-b15'>
                                                {i18n.t('campaigns:standard.step3.mustVerifyAccountEmail')}{' '}
                                                <strong>{user?.userData?.emailAddress}</strong>{' '}
                                                {i18n.t('campaigns:standard.step3.beforeYouCanSendPreview')}
                                            </div>
                                            <Button
                                                className='button-sml min-shadow outline red'
                                                text={i18n.t('campaigns:abSplitTest.send.preview.verifyAddress')}
                                                onClick={() => {
                                                    if (history) history.push('/cp/company/domains')
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Paper>
                        </div>
                    )
                })}
            </div>
        </Paper>
    )
}
